import { useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, useSearchParams } from "react-router-dom";
import BrainwaveGenerator from "./components/BrainwaveGenerator";
import LandingPage from "./components/LandingPage";
import SciencePage from "./components/SciencePage";
import AuthCallback from "./components/AuthCallback";
import VerifyEmail from "./components/VerifyEmail";
import ResetPassword from "./components/ResetPassword";
import ResendVerification from "./components/ResendVerification";
import AdminPanel from "./components/AdminPanel";
import OfflineIndicator from "./components/OfflineIndicator";
import UpdatePrompt from "./components/UpdatePrompt";
import VersionStamp from "./components/VersionStamp";
import { Toaster } from "./components/ui/toaster";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { initConsoleCapture } from "./lib/diagnostics";
// Side-effect import: registers the global beforeinstallprompt listener at
// module-load so the event isn't missed by component-level useEffects that
// mount after the browser has already fired it (Brave/Firefox timing).
import "./lib/pwaInstall";

// Capture console + window errors at module load — needs to run before any
// other code logs so the snapshot can include earliest startup activity.
initConsoleCapture();

const AppPage = () => {
  const { loading } = useAuth();
  const [searchParams] = useSearchParams();

  // Capture ?ref=CODE into localStorage on arrival so we can apply it after
  // auth (whether the user registers, logs in, or OAuths). One-shot.
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref && /^[A-Z0-9-]{4,20}$/i.test(ref)) {
      try { localStorage.setItem('neurowave_pending_ref_code', ref.toUpperCase()); } catch { /* ignore */ }
    }
  }, [searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Handle email verification
  const verifyToken = searchParams.get('verify');
  if (verifyToken) {
    return <VerifyEmail token={verifyToken} />;
  }

  // Handle password reset
  const resetToken = searchParams.get('reset');
  if (resetToken) {
    return <ResetPassword token={resetToken} />;
  }

  // Handle resend-verification as a query param (?resend=1 or ?resend=email@x.com)
  const resendFlag = searchParams.get('resend');
  if (resendFlag) {
    const initialEmail = resendFlag.includes('@') ? resendFlag : '';
    return <ResendVerification initialEmail={initialEmail} />;
  }

  return <BrainwaveGenerator />;
};

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/science" element={<SciencePage />} />
      <Route path="/app" element={<AppPage />} />
      <Route path="/resend-verification" element={<ResendVerification />} />
      <Route path="/admin" element={<AdminPanel />} />
    </Routes>
  );
}

function App() {
  // The SW that has finished installing and is *waiting* to take over.
  // null means we're on the freshest version (or no SW activity yet).
  const [waitingWorker, setWaitingWorker] = useState(null);
  // User-dismissed flag — we honour it for this tab/session only. On full
  // reload (or next session) we re-prompt if a waiting SW still exists, so
  // we don't strand the user on stale code forever.
  const [updateDismissed, setUpdateDismissed] = useState(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    let cancelled = false;
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registration) => {
        if (cancelled) return;

        // If a SW is already waiting on first registration (e.g., previous
        // tab loaded an update that the user never reloaded), surface it.
        if (registration.waiting && navigator.serviceWorker.controller) {
          setWaitingWorker(registration.waiting);
        }

        // Watch for new updates installed while the tab is open.
        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            // `installed` + an existing controller means this is an UPDATE
            // (not the very first SW install on a fresh device).
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              setWaitingWorker(installing);
            }
          });
        });

        // Poll for updates every 30 min while the tab is open. PWAs left
        // open on a phone for days otherwise never check.
        const updateInterval = setInterval(() => {
          registration.update().catch(() => {});
        }, 30 * 60 * 1000);
        return () => clearInterval(updateInterval);
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider>
            <SubscriptionProvider>
              <AppRouter />
            </SubscriptionProvider>
          </ThemeProvider>
        </AuthProvider>
        <Toaster />
        <OfflineIndicator />
        <UpdatePrompt
          waitingWorker={updateDismissed ? null : waitingWorker}
          onClose={() => setUpdateDismissed(true)}
        />
        <VersionStamp />
      </BrowserRouter>
    </div>
  );
}

export default App;
